import React, { useCallback, useEffect, useMemo, useState } from "react";

// Daemon 에서 제어값 변경 - KJ
// import { getObj, updateObj } from "pages/Admin/SiteEdit/apis/adminSiteEditApis";
import { getObj } from "pages/Admin/SiteEdit/apis/adminSiteEditApis";
import { getCombo } from "pages/Admin/User/apis/adminUserApis";

import { Flex, Select } from "@mantine/core";
import { comboListState } from "components/Form/atom/groupListState";
import { getSiteList } from "pages/Admin/Site/apis/adminSiteApis";
import ObjectItem from "pages/Admin/SiteEdit/components/ObjectItem";
import { userState } from "pages/Login/atom/userState";
import { useLocation } from 'react-router-dom';
import { useRecoilState } from "recoil";
import { useSrdkoreaSocket } from "../../../ws/useSrdkoreaSocket";
import styled from "styled-components";

// SearchBar 컴포넌트를 함수 외부로 이동
const SearchBar = React.memo(({ groupList, fieldList, selectGroupId, selectFieldId, onGroupChange, onFieldChange }) => {
  return (
    <Flex gap={15} mb={10} align="center"
      style={{
        width: "90%",
      }}
    >
      <Select
        data={groupList ?? []}
        value={selectGroupId}
        placeholder="그룹선택"
        onChange={onGroupChange}
      />
      <Select
        data={fieldList ?? []}
        value={selectFieldId}
        placeholder="현장선택"
        onChange={onFieldChange}
      />
    </Flex>
  );
});

// Contact 컴포넌트도 함수 외부로 이동
const Contact = React.memo(({ officePhone }) => {
  return (
    <Flex gap={15} mb={10} mt={10} ml={10}
      style={{
        width: "100%",
        fontSize: 14,
        background: "white",
      }}
    >
      연락처 : {officePhone ?? '--- ---- ---'}
    </Flex>
  );
});

const parseHexToDecimal = (value) => {
  return parseInt(value, 16);
};

const parseAlarm = (alarm, messageKey, expectedLength) => {
  const raw = alarm?.[messageKey];

  if (!raw) return null;

  // 상태값 128 bytes = 256 hex characters
  // 제어값 64 bytes = 128 hex characters
  if (raw.length !== expectedLength) {
    console.warn(
      `[parseAlarm] 잘못된 전문 길이: ${raw.length}자`
    );
    return null;
  }

  // HEX 문자열인지 확인
  if (!/^[0-9a-fA-F]+$/.test(raw)) {
    console.warn("[parseAlarm] HEX 형식이 아닙니다.");
    return null;
  }

  return {
    csn: raw.substring(0, 20),
    len: parseHexToDecimal(raw.substring(20, 24)),
    raw,
    data: (raw.substring(24).match(/.{4}/g) || [])
      .map((value, index) => ({
        index: index + 7,
        hex: value,
        decimal: parseHexToDecimal(value),
      })),
  };
};

const formatAlarmTime = (date = new Date()) => {
  const yy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const InfoTable = styled.table`
  width: 1600px;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 20px;

  td {
    width: 20%;
    border: 1px solid #000;
    text-align: left;
    padding: 10px;
  }
`;

function HomeForm() {
  const [initialGroupList, setInitialGroupList] =  useRecoilState(comboListState);
  const location = useLocation();
  let linkedGroupId = null;
  let linkedFieldId = null;
  if (location.state ) {
    linkedGroupId = location.state.linkGroupId;
    linkedFieldId = location.state.linkFieldId;
  }
  const [user] = useRecoilState(userState);

  const [fieldList, setFieldList] = useState([]);
  const [selectGroupId, setSelectGroupId] = useState(linkedGroupId ?? user.fn_group_id);
  const [selectFieldId, setSelectFieldId] = useState(linkedFieldId ?? user.field_id);

  const [defaultObjectState, setDefaultObjectState] = useState();
  // const [countdown, setCountdown] = useState(0);
  // const [pauseApiCall, setPauseApiCall] = useState(false);

  const [bsdIds, setBsdIds] = useState([]);
  const [lastAlarm, setLastAlarm] = useState(null);
  const [lastAlarmMap, setLastAlarmMap] = useState({});
  const [commList, setCommList] = useState([]);
  const isDebug = new URLSearchParams(location.search).get("debug") === "y";

  useSrdkoreaSocket(
    user ? `srdkorea_${user.user_id}` : null,
    bsdIds,
    null,
    (alarm) => {
      const alarmTime = formatAlarmTime();
      const alarmBsdId = alarm.bsdId.replace("bsd_", "");

      setLastAlarm({
        time: alarmTime,
        data: alarm,
      });

      setLastAlarmMap((prev) => ({
        ...prev,
        [alarmBsdId]: alarmTime,
      }));

      const parsedSttus = parseAlarm(alarm, "message_sttus", 256);
      const parsedCtrl = parseAlarm(alarm, "message_ctrl", 128);

      if (!parsedSttus && !parsedCtrl) return;

      const bsdId = alarm.bsdId.replace("bsd_", "");

      setCommList((prev) => {
        const exists = prev.find(
          (item) => item.bsd_id === bsdId
        );

        if (exists) {
          return prev.map((item) =>
            item.bsd_id === bsdId
              ? {
                  ...item,
                  bsd_nm: alarm.bsd_nm ?? item.bsd_nm,
                  status: " 통신정상",
                  color: "blue",
                }
              : item
          );
        }

        return [
          ...prev,
          {
            bsd_id: bsdId,
            bsd_nm: alarm.bsd_nm ?? "",
            status: " 통신정상",
            color: "blue",
          },
        ];
      });

      const sttusMap = parsedSttus
        ? new Map(
            parsedSttus.data.map((ch) => [
              String(ch.index),
              String(ch.decimal),
            ])
          )
        : null;

      const ctrlMap = parsedCtrl
        ? new Map(
            parsedCtrl.data.map((ch) => [
              String(ch.index),
              String(ch.decimal),
            ])
          )
        : null;

      setObjectList((prev) =>
        prev.map((item) => {
          if (item.bsd_id !== bsdId) return item;

          const nextItem = { ...item };

          if (sttusMap && item.sttus_chnnl_no) {
            const value = sttusMap.get(
              String(item.sttus_chnnl_no)
            );

            if (value !== undefined) {
              nextItem.chnnl_sttus_value = value;
            }
          }

          if (ctrlMap && item.chnnl_no) {
            const value = ctrlMap.get(
              String(item.chnnl_no)
            );

            if (value !== undefined) {
              nextItem.chnnl_ctrl_value = value;
            }
          }

          return nextItem;
        })
      );
    }
  );

  // location.state에서 전달된 그룹/현장 ID가 변경되면 업데이트
  useEffect(() => {
    if (location.state?.linkGroupId) {
      setSelectGroupId(location.state.linkGroupId);
    }
    if (location.state?.linkFieldId) {
      setSelectFieldId(location.state.linkFieldId);
    }
  }, [location.state?.linkGroupId, location.state?.linkFieldId]);

  useEffect(() => {
    getCombo().then((res) => {
      setInitialGroupList(res);
    });
  }, [setInitialGroupList]);

  const groupList = useMemo(() => {
    return initialGroupList
      ?.filter((item) => item.id === "group")
      .map((item) => ({
        value: item.code,
        label: item.name,
      }));
  }, [initialGroupList]);

  useEffect(() => {
    getSiteList(100, 1, selectGroupId ?? null).then((res) => {
      const filteredList = res.filter(
        (item) => item.group_id === selectGroupId
      );
      const mappedList = filteredList.map((item) => ({
        value: item.field_id,
        label: item.field_nm,
        object_id: item.object_id ?? "",
        user_id: item.field_manager_id,
        group_id: item.group_id,
        office_phone: item.office_phone ,
      }));

      setFieldList(mappedList);
      // 마커 클릭으로 전달된 linkedFieldId가 있으면 해당 현장 선택, 없으면 첫 번째 현장 자동 선택
      const currentLinkedFieldId = location.state?.linkFieldId;
      if (mappedList.length > 0) {
        if (currentLinkedFieldId && mappedList.find(field => field.value === currentLinkedFieldId)) {
          // linkedFieldId가 현재 그룹의 현장 목록에 있으면 해당 현장 선택
          setSelectFieldId(currentLinkedFieldId);
        } else {
          // linkedFieldId가 없거나 해당 그룹에 없으면 첫 번째 현장 선택
          setSelectFieldId(mappedList[0].value);
        }
      }
    });
    //
  }, [selectGroupId, location.state?.linkFieldId]);

  useEffect(() => {
    setDefaultObjectState(
      fieldList.find((field) => field.value === selectFieldId)
    );
  }, [fieldList, selectFieldId]);

  const [objectList, setObjectList] = useState([]);
  const [imgyeList, setImgyeList] = useState([]);

  useEffect(() => {
    if (!defaultObjectState) return;

    let wasHidden = document.visibilityState === "hidden";

    const fetchData = async () => {
      try {
        const response = await getObj(
          defaultObjectState.value,
          defaultObjectState.object_id
        );

        const bsdIds = [
          ...new Set(
            response.view
              .filter((item) => item.bsd_id)
              .map((item) => `bsd_${item.bsd_id}`)
          ),
        ];

        setBsdIds(bsdIds);

        const sortedList = [...response.view].sort((a, b) => {
          if (a.obj_seq !== b.obj_seq) {
            return a.obj_seq - b.obj_seq;
          }

          return a.obj_id - b.obj_id;
        });

        setObjectList(sortedList);
        setImgyeList(response.imgye);
      } catch (error) {
        console.error("getObj error:", error);
      }
    };

    // 최초 진입
    fetchData();

    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";

      // 백그라운드 → 포그라운드
      if (wasHidden && isVisible) {
        console.log(
          "[HomeForm] 백그라운드 → 포그라운드, 데이터 재조회"
        );

        fetchData();
      }

      wasHidden = !isVisible;
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [defaultObjectState]);

  const StyledWrap = useMemo(() => {
    return React.memo(({ children }) => <Styled.Wrap>{children}</Styled.Wrap>);
  }, []);

  const handleGroupChange = useCallback((value) => setSelectGroupId(value), []);
  const handleFieldChange = useCallback((value) => setSelectFieldId(value), []);

  // console.log("pauweApi", pauseApiCall);
  // console.log("countDown", countdown);

  return (
    <div>
      <Flex >
        <div> <SearchBar groupList={groupList} fieldList={fieldList} selectGroupId={selectGroupId} selectFieldId={selectFieldId} onGroupChange={handleGroupChange} onFieldChange={handleFieldChange} /> </div>
        <div> <Contact officePhone={defaultObjectState?.office_phone} /> </div>
      </Flex>
      <StyledWrap>
        {objectList?.map((object) => (
          <div
            key={object.obj_id}
            style={{
              left: object.left,
              top: object.top,
              position: "absolute",
            }}
          >
            <ObjectItem
              id={object.obj_id}
              object={object}
              objectList={objectList}
              setObjectList={setObjectList}
              imgyeList={imgyeList}
              commList={commList}
              setCommList={setCommList}
              // setCountdown={setCountdown}
              // setPauseApiCall={setPauseApiCall}
            />
          </div>
        ))}
      </StyledWrap>

      <InfoTable>
        <tbody>
          <tr>
            {Array.from({ length: 5 }).map((_, index) => {
              const bsdId = bsdIds[index];

              const bsdData = commList?.find(
                (item) => `bsd_${item.bsd_id}` === bsdId
              );

              const alarmTime = lastAlarmMap[bsdData?.bsd_id];

              return (
                <td key={index} id={bsdId || `empty_${index}`}>
                  <div>{bsdData?.bsd_nm || "-"}</div>

                  <div>{alarmTime || "-"}</div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </InfoTable>

      {isDebug && lastAlarm && (() => {
        const parsedSttus = parseAlarm(lastAlarm.data, "message_sttus", 256);
        const parsedCtrl = parseAlarm(lastAlarm.data, "message_ctrl", 128);

        return (
          <div>
            <h3>Latest Alarm</h3>

            <div>
              <b>{lastAlarm.data.bsdId}: </b>
              <span>{lastAlarm.time}</span>
            </div>

            {/* Status */}
            {parsedSttus && (
              <div>
                <h4>Status</h4>

                <div>
                  <span>
                    CSN: {parsedSttus.csn} / Payload Data Length:{" "}
                    {parsedSttus.len}
                  </span>
                </div>

                <div>
                  <span>
                    {lastAlarm.data.message_sttus}
                  </span>
                </div>

                <div>
                  {parsedSttus.data.map((item) => (
                    <div key={item.index}>
                      CH {item.index} : {item.hex} → {item.decimal}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Control */}
            {parsedCtrl && (
              <div>
                <h4>Control</h4>

                <div>
                  <span>
                    CSN: {parsedCtrl.csn} / Payload Data Length:{" "}
                    {parsedCtrl.len}
                  </span>
                </div>

                <div>
                  <span>
                    {lastAlarm.data.message_ctrl}
                  </span>
                </div>

                <div>
                  {parsedCtrl.data.map((item) => (
                    <div key={item.index}>
                      CH {item.index} : {item.hex} → {item.decimal}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default HomeForm;

const Styled = {
  Wrap: styled.div`
    width: 1600px;
    height: 800px;
    border: 1px solid black;
    position: relative;

    background-color: #ffffff;
    background-size: 10px 10px;
  `,
};
